<template>
    <div>
        <vue-headful :title="'Account Settings :: MeritEHS'"/>

        <h4 class="py-3 mb-4">
            <ol class="breadcrumb font-weight-bold m-0">
                <li class="breadcrumb-item font-weight-light"><i class="fa fa-cogs"></i> Admin</li>
                <li class="breadcrumb-item active">Account Settings</li>
            </ol>
        </h4>

        <ul class="search-nav nav nav-tabs tabs-alt container-p-x container-m--x mb-4 d-lg-flex d-none">
            <b-nav-item :active="curTab === 'general'" @click="curTab = 'general'">
                General Settings
            </b-nav-item>
            <b-nav-item :active="curTab === 'branding'" @click="curTab = 'branding'">
                Branding
            </b-nav-item>
            <b-nav-item :active="curTab === 'sso'" @click="curTab = 'sso'">
                Single-Sign On
            </b-nav-item>
        </ul>

        <div class="mb-2 mb-lg-0 d-lg-none d-block">
            <b-dropdown :html="getTabTitle" variant="default" class="bg-white">
                <b-dropdown-item :active="curTab === 'general'" @click="curTab = 'general'">
                    General Settings
                </b-dropdown-item>
                <b-dropdown-item :active="curTab === 'branding'" @click="curTab = 'branding'">
                    Branding
                </b-dropdown-item>
                <b-dropdown-item :active="curTab === 'sso'" @click="curTab = 'sso'">
                    Single-Sign On
                </b-dropdown-item>
            </b-dropdown>
        </div>

        <div class="row">
            <div class="col-xl-8 col-lg-12">

                <div v-if="curTab === 'general'">

                    <div class="card mb-3">
                        <div class="card-header">
                            <h6>JSA Defaults</h6>
                        </div>
                        <div class="card-body">

                            <div class="form-group row">
                                <label class="col-md-3 col-sm-4 col-form-label text-sm-right">
                                    Standard PPE:
                                </label>
                                <div class="col-sm-8">
                                    <b-form-textarea v-model="account.standard_ppe" rows="2" placeholder="Enter Standard PPE text for use on JSAs"></b-form-textarea>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="card mb-3">
                        <div class="card-header">
                            <h6>Role Request Setting</h6>
                        </div>
                        <div class="card-body">
                            <div class="alert alert-info">
                                These are the users who will receive the role request emails. They must be admins in order to approve requests.
                            </div>
                            <div class="form-group row">
                                <label class="col-md-3 col-sm-4 col-form-label text-sm-right">
                                    Role Request Approvers:
                                </label>
                                <div class="col-sm-8">
                                    <user-select ref="roleApprover" @vuselected="onRoleApproverSelected" />
                                </div>
                            </div>
                            <div class="col-md-8 offset-md-1">
                                <ul>
                                    <li v-for="user in account.user_admins">
                                        <a href="javascript:void(0)" class="text-danger" :title="`Remove ${user.name} from Role Approvers`" v-on:click="removeRoleApprover(user)">
                                            <i class="fa fa-remove"></i>
                                        </a>
                                        {{ user.name }}
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <button type="button" class="btn btn-success" @click="saveSettings()" :disabled="saving">
                        Update Settings
                    </button>

                </div>
                <div v-if="curTab === 'branding'">

                    <div class="card mb-3">
                        <div class="card-header">
                            <strong>Subdomain</strong>
                        </div>
                        <div class="card-body">
                            <div v-if="loading" class="text-center">
                                <i class="fa fa-circle-o-notch fa-spin"></i> Loading
                            </div>
                            <div v-else>
                                <div class="form-group row">
                                    <label :class="{ 'col-lg-3 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.subdomain }">
                                        Subdomain:
                                    </label>
                                    <div class="col-md-6">
                                        <b-form-input type="text" v-model="account.subdomain" :class="{'is-invalid': errors.subdomain}" placeholder="(Optional). Allows you to brand the urls with your company name."></b-form-input>
                                        <div class="mt-2 text-info">
                                            Use this to brand your instance of MeritEHS. Example: https://<strong>{{ account.subdomain ? account.subdomain : 'mycompany' }}</strong>.meritehs.com. Do not enter the full URL nor URL punctuation.
                                        </div>
                                        <div v-if="errors.subdomain" class="text-danger mt2">
                                            <span v-for="error in errors.subdomain">{{ error }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <template v-if="account.subdomain">
                        <div class="card mb-3">
                            <div class="card-header">
                                <strong>
                                    Login Brand Image <i class="fa fa-question-circle" title="This will be shown on the login page"></i>
                                </strong>
                            </div>
                            <div class="card-body">
                                <div class="form-group">
                                    <b-form-file accept="image/*" plain v-model="brand_logo_large"/>

                                    <div class="alert alert-warning mt-2">
                                        This will be displayed on your login page if you use a Subdomain to white-label your account.
                                    </div>

                                    <div class="mt-3 py-2 px-2" v-if="show_login_logo" style="border: 1px dashed #ececec; overflow-x: auto; overflow-y: auto">
                                        <img v-bind:src="account.brand_logo_large" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="card mb-3" v-if="false">
                            <div class="card-header">
                                <strong>
                                    App Icon Brand Image <i class="fa fa-question-circle" title="This will be shown in the upper left corner of the application"></i>
                                </strong>
                            </div>
                            <div class="card-body">
                                <b-form-file accept="image/*" plain @change="setImage"/>
                                <div class="alert alert-warning mt-2">
                                    This will replace the default MeritEHS logo in the upper-left corner of the application for logged in users.
                                </div>

                                <div class="my-2">
                                    <label>Preview:</label>
                                    <img :src="cropImg" style="width: 30px; height: 30px; border: 1px solid gray" alt="CROP" />
                                </div>
                                <div>
                                    <vue-cropper
                                    ref='cropper'
                                    :cropend="cropImage"
                                    :ready="cropImage"
                                    :guides="true"
                                    :view-mode="2"
                                    :auto-crop-area="1"
                                    drag-mode="crop"
                                    :min-container-width="200"
                                    :min-container-height="200"
                                    :background="true"
                                    :src="account.brand_logo"
                                    alt="Source Image"
                                    :aspectRatio="1"
                                    :img-style="{ 'width': '400px', 'height': '400px' }">
                                    </vue-cropper>
                                </div>
                            </div>
                        </div>
                    </template>

                    <div class="card mb-3">
                        <div class="card-header">
                            <strong>PDF Logo <i class="fa fa-question-circle" title="This is the logo that will be used for PDF Exports."></i></strong>
                        </div>
                        <div class="card-body">
                            <b-form-file accept="image/*" plain v-model="pdf_logo"/>

                            <div class="mt-3 py-2 px-2" v-if="show_pdf_logo" style="border: 1px dashed #ececec; overflow-x: auto; overflow-y: auto">
                                <img v-bind:src="this.account.inci_pdf_logo" />
                            </div>
                        </div>
                    </div>

                    <button type="button" class="btn btn-success" @click="saveSettings()" :disabled="saving">
                        Update Settings
                    </button>
                    <button type="button" class="btn btn-warning pull-right" @click="resetDefault" :disabled="saving" v-if="false">
                        Reset to Defaults
                    </button>

                </div>
                <div v-if="curTab === 'sso'">

                    <div class="card mb-4">
                        <div class="card-header">
                            <strong>Single-Sign On</strong>
                        </div>
                        <div class="card-body">
                            <div class="form-group row">
                                <label :class="{ 'col-lg-2 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.account_domain }">
                                    Email Domain:
                                </label>
                                <div class="col">
                                    <b-form-input disabled type="text" :value="account.account_domain" :class="{'is-invalid': errors.account_domain}"></b-form-input>
                                        <small>If this domain does not match your Single-Sign On domain, contact us <router-link to="/bug">here</router-link> with your correct domain in the message to get it fixed.</small>
                                    <span v-if="errors.account_domain" class="text-danger">
                                        <br>
                                        <span v-for="error in errors.account_domain">{{ error }}</span>
                                    </span>
                                </div>
                            </div>

                            <div class="form-group row">
                                <label class="col-lg-2 col-md-3 col-sm-4 col-form-label text-sm-right">
                                    Login Type:
                                </label>
                                <div class="col">
                                    <b-form-select v-model="account.login_type">
                                        <option value="basic">Basic</option>
                                        <template v-if="account.account_domain">
                                            <option value="graph">Microsoft Office</option>
                                        </template>
                                    </b-form-select>
                                </div>
                            </div>

                            <div class="form-group row" v-if="account.login_type === 'graph'">
                                <label class="col-lg-4 col-md-4 col-sm-6 col-form-label text-sm-right">
                                    Enable SSO to Auto-Create Users: <i class="fa fa-question-circle" title="If checked, MeritEHS will auto-create users when they attempt to login via SSO. Permissions will be auto-assigned by matching the SSO Job Title to the assigned match values on any Role."></i>
                                </label>
                                <div class="col-md-6 col-sm-8 col-12">
                                    <b-form-checkbox v-model="account.enable_sso_user_creation" :value="1" :unchecked-value="0" plain />
                                </div>
                            </div>
                        </div>
                        <div class="card-footer">
                            <button type="button" class="btn btn-success" @click="saveSettings()" :disabled="saving">
                                Update Settings
                            </button>
                        </div>
                    </div>

                </div>
            </div>
        </div>

        <!--
        <div class="card mt-3">
            <div class="card-header">
                <h4 class="mb-0">OSHA API Settings</h4>
            </div>
            <div class="card-body">
                <div v-if="loading" class="text-center">
                    <i class="fa fa-circle-o-notch fa-spin"></i> Loading
                </div>
                <div v-else>
                    <div class="form-group row">
                        <label class="col-md-3 col-sm-4 col-form-label text-sm-right">
                            OSHA API Token:
                        </label>
                        <div class="col-sm-8">
                            <b-form-textarea v-model="account.osha_key" rows="4"></b-form-textarea>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        -->
    </div>
</template>

<script>
    export default {
        data: function() {
            return {
                saving: false,
                unsaved_colors: false,
                loading: true,
                curTab: 'general',
                account: {},
                original_user: {}, // The original user object, with original colors
                errors: [],
                cropImg: '',
                brand_logo_large: null,
                pdf_logo: null,
                show_login_logo: false,
                show_pdf_logo: false,
            }
        },

        computed: {
            getTabTitle(){
                //Setup
                var title = '';
                var icon = 'bars';

                //Switch
                switch(this.curTab)
                {
                    case 'general':
                        //Set
                        title = 'General Settings';
                        break;
                    case 'branding':
                        //Set
                        title = 'Branding';
                        break;
                    case 'sso':
                        //Set
                        title = 'Single-Sign on';
                        break;
                }

                //Send it
                return '<i class="fa fa-'+icon+'"></i> ' + title;
            }
        },

        watch: {
            curTab(newValue, oldValue) {
                if (newValue)
                {
                    if (newValue === 'general')
                    {
                        window.history.replaceState(null,null,`${this.$route.path}`)
                    }
                    else
                    {
                        window.history.replaceState(null,null,`${this.$route.path}#${this.curTab}`)
                    }
                }
            },
        },
        methods: {
            getSettings() {
                //Get the data
                this.loading = true;
                this.$http
                    .get('admin/settings')
                    .then(response => {
                        //Set all the data
                        this.account = response.data;
                        this.account.brand_logo = this.account.brand_logo + '?' + new Date().getTime();

                        //Set the image
                        if(this.account.brand_logo_large)
                        {
                            //Set the values
                            this.show_login_logo = true;
                        }

                        //Set the image
                        if(this.account.inci_pdf_logo)
                        {
                            //Set the values
                            this.show_pdf_logo = true;
                        }

                        //Set loading
                        this.loading = false;
                    })
                    .catch(response => {

                    });
            },

            setImage(e) {
                //Set the file
                const file = e.target.files[0];

                //Check the type
                if (file.type.indexOf('image/') === -1)
                {
                    alert('Please select an image file');
                    return;
                }

                //Check for a function
                if (typeof FileReader === 'function')
                {
                    const reader = new FileReader();
                    reader.onload = (event) => {
                        this.imgSrc = event.target.result;
                        // rebuild cropperjs with the updated source
                        this.$refs.cropper.replace(event.target.result);
                    };
                    reader.readAsDataURL(file);
                } else {
                    alert('Sorry, FileReader API not supported');
                }
            },

            cropImage() {
                // get image data for post processing, e.g. upload or setting image src
                this.cropImg = this.$refs.cropper.getCroppedCanvas().toDataURL();
            },

            previewColors() {
                this.unsaved_colors = true;
                let user = this.$store.state.user;
                user.brand_color = this.account.brand_color;
                user.brand_text_color = this.account.brand_text_color;
                this.$store.commit('login', JSON.parse(JSON.stringify(user)));
            },

            resetColors() {
                this.unsaved_colors = false;
                this.$store.commit('login', JSON.parse(JSON.stringify(this.original_user)));
                this.account.brand_color = this.original_user.brand_color;
                this.account.brand_text_color = this.original_user.brand_text_color;
            },

            // Logo gets defaulted on the server, colors get defaulted here
            resetDefault() {
                if (confirm('Are you sure you want to reset your brand logo and colors to the system defaults?'))
                {
                    this.unsaved_colors = false;
                    let user = this.$store.state.user;
                    user.brand_color = '#2f3337';
                    user.brand_text_color = '#ffffff';
                    this.$store.commit('login', JSON.parse(JSON.stringify(user)));

                    this.account.brand_color = user.brand_color;
                    this.account.brand_text_color = user.brand_text_color;
                    this.saveSettings(true);
                }
            },

            putSettingsInStore(user) {
                user.brand_color = this.account.brand_color;
                user.brand_text_color = this.account.brand_text_color;
                user.standard_ppe = this.account.standard_ppe;

                return user;
            },

            saveSettings(reset = false) {
                //SHow loading
                this.saving = true;

                //Check for undefined or null
                if(!this.$refs.cropper)
                {
                    //Send it
                    this.postSettings(false, null);

                    //Stop Here
                    return;
                }

                //Include Files
                this.account.includes_files = 1;

                //Get the Cropped Image if Applicable
                this.$refs.cropper.getCroppedCanvas().toBlob(blob => {
                    //Send it
                    this.postSettings(reset, blob);
                });
            },

            //Send it
            postSettings(reset, blob){
                //Setup Files
                var files = {};

                //Reset the errors
                this.errors = [];

                //Append Standard Brand Logo
                if(!reset && this.account.includes_files !== 0 && blob)
                {
                    //Append
                    files['brand_logo'] = blob;
                }

                //Append Brand Logo Large
                if(this.brand_logo_large)
                {
                    //Append
                    files['brand_logo_large'] = this.brand_logo_large;
                }

                //Check PDF File
                if(this.pdf_logo)
                {
                    files['pdf_logo'] = this.pdf_logo;
                }

                //Send it
                this.$http
                    .postWithFiles('admin/settings', this.account, files)
                    .then(response => {
                        //Alert the user?
                        this.$toastr.success('Settings Saved');

                        //Update the File Paths
                        this.account.brand_logo_large = response.data.brand_logo_large;
                        this.account.inci_pdf_logo = response.data.inci_pdf_logo;

                        //Reset some stuff
                        this.brand_logo_large = null;
                        this.pdf_logo = null;

                        //Setup the user
                        let user = this.$store.state.user;

                        //Existing Subdomain
                        let oldSubdomain = user.subdomain;

                        //Set the Variables
                        if(this.account.includes_files === 1)
                        {
                            //Set
                            user.brand_logo = response.data.brand_logo + '?' + new Date().getTime();
                        }

                        //Set the other values
                        user.brand_name = response.data.brand_name;
                        user.brand_name_short = response.data.brand_name_short;
                        user.subdomain = response.data.subdomain;

                        //Update the store
                        user = this.putSettingsInStore(user);
                        this.$store.commit('login', JSON.parse(JSON.stringify(user)));

                        // Reset what the "original" is
                        this.original_user = JSON.parse(JSON.stringify(this.$store.state.user));

                        // We no longed have unsaved colors
                        this.unsaved_colors = false;

                        //Check if we need a redirect
                        if(oldSubdomain !== user.subdomain)
                        {
                            //Redirect via a hard reload
                            window.location.reload();
                        }
                    })
                    .catch(error => {
                        //Check for error
                        if (error.response.status === 422) {
                            //Display validation errors
                            this.errors = error.response.data
                        }
                    })
                    .then(response => {
                        //SHow loading
                        this.saving = false;
                        delete this.account.includes_files;
                    });
            },

            onRoleApproverSelected(user){
                //Make sure we didn't just clear the user
                if(user == null || Object.keys(user).length == 0)
                {
                    return;
                }

                //Setup
                var user_admins = [];

                //If the user_admins is null, we need to set it
                if(this.account.user_admins != null)
                {
                    user_admins = this.account.user_admins;
                }

                //Push the user onto the user_admins if exists
                for(var i in user_admins)
                {
                    //Check against selected user
                    if(user_admins[i].user_id == user.UserId)
                    {
                        //Reset the user select
                        this.$refs.roleApprover.reset();

                        //Do nothing as this user already exists
                        return;
                    }
                }

                //If we make it here, that means the user does not exists. Add it
                user_admins.push({user_id: user.UserId, name: (user.FirstName+' '+user.LastName)});

                //Set it
                this.account.user_admins = user_admins;

                //Reset the user select
                this.$refs.roleApprover.reset();
            },

            removeRoleApprover(user){
                //Push the user onto the user_admins if exists
                for(var i in this.account.user_admins)
                {
                    //Check against selected user
                    if(this.account.user_admins[i].user_id == user.user_id)
                    {
                        //Splice it
                        this.account.user_admins.splice(i, 1);

                        //Do nothing as this user already exists
                        return;
                    }
                }
            },
        },

        mounted() {
            this.getSettings();
            this.original_user = JSON.parse(JSON.stringify(this.$store.state.user));

            if (this.$route.hash !== '' && this.$route.hash !== '#')
            {
                this.curTab = this.$route.hash.replace('#','')
            }
        },

        beforeRouteLeave (to, from, next) {
            if (this.unsaved_colors)
                this.resetColors()

            next();
        }
    }
</script>